import React, { useState } from 'react';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import { api } from '../../../lib/api';
import { useToast } from '../../../components/ui/Toast';
import { Button, Card } from '../../../components/ui/Kit';
import Modal from '../../../components/ui/Modal';
import SafeIcon from '../../../common/SafeIcon';
import { calculateNetWorth } from '../../../lib/financialAnalysisUtils';

const ASSET_CATEGORIES = [
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'investments', label: 'Investments' },
  { value: 'checking', label: 'Checking' },
  { value: 'savings', label: 'Savings' },
  { value: 'vehicles', label: 'Vehicles' },
  { value: 'retirement', label: 'Retirement' },
  { value: '401k_403b', label: '401(k) / 403(b)' },
  { value: 'ira_sep', label: 'IRA / SEP' },
  { value: 'roth_ira', label: 'Roth IRA' },
  { value: 'cash_value_insurance', label: 'Cash Value Insurance' },
  { value: 'crypto', label: 'Crypto' },
  { value: 'mutual_funds', label: 'Mutual Funds' },
  { value: 'etf', label: 'ETF' },
  { value: 'other', label: 'Other' },
];

const LIABILITY_CATEGORIES = [
  { value: 'mortgage', label: 'Mortgage' },
  { value: 'auto_loan', label: 'Auto Loan' },
  { value: 'student_loan', label: 'Student Loan' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'personal_loan', label: 'Personal Loan' },
  { value: 'other', label: 'Other' },
];

const cellInput =
  'w-full h-9 rounded-md border border-gray-200 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent';

function formatCurrency(val) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(Number(val) || 0);
}

function assetPayloadFromRow(row) {
  return {
    category: row.category || 'other',
    description: row.description || '',
    amount: Number(row.amount) || 0,
  };
}

function liabilityPayloadFromRow(row) {
  return {
    category: row.category || 'other',
    description: row.description || '',
    amount: Number(row.amount) || 0,
    interest_rate:
      row.interest_rate === '' || row.interest_rate == null
        ? null
        : Number(row.interest_rate),
  };
}

export default function BalanceSheetTab({
  analysisId,
  agencyId,
  assets,
  liabilities,
  setAssets,
  setLiabilities,
  scheduleRowSave,
  setSaveStatus,
  markSavedBriefly,
  assetsRef,
  liabilitiesRef,
}) {
  const { addToast } = useToast();
  const [adding, setAdding] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const totalAssets = (assets || []).reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
  const totalLiabilities = (liabilities || []).reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
  const netWorth = calculateNetWorth(assets, liabilities);

  const onAssetChange = (rowId, field, value) => {
    setAssets((prev) => {
      const next = prev.map((r) => (r.id === rowId ? { ...r, [field]: value } : r));
      assetsRef.current = next;
      return next;
    });
    scheduleRowSave('assets', rowId, () => {
      const row = (assetsRef.current || []).find((r) => r.id === rowId);
      return assetPayloadFromRow(row || {});
    });
  };

  const onLiabilityChange = (rowId, field, value) => {
    setLiabilities((prev) => {
      const next = prev.map((r) => (r.id === rowId ? { ...r, [field]: value } : r));
      liabilitiesRef.current = next;
      return next;
    });
    scheduleRowSave('liabilities', rowId, () => {
      const row = (liabilitiesRef.current || []).find((r) => r.id === rowId);
      return liabilityPayloadFromRow(row || {});
    });
  };

  const addAsset = async () => {
    setAdding('asset');
    try {
      const row = await api.financialAnalysis.assets.create(analysisId, {
        agency_id: agencyId,
        category: 'checking',
        description: '',
        amount: 0,
      });
      setAssets((prev) => {
        const next = [...prev, row];
        assetsRef.current = next;
        return next;
      });
      markSavedBriefly();
    } catch (err) {
      addToast({ type: 'error', title: 'Could not add asset', message: err?.message });
    } finally {
      setAdding(null);
    }
  };

  const addLiability = async () => {
    setAdding('liability');
    try {
      const row = await api.financialAnalysis.liabilities.create(analysisId, {
        agency_id: agencyId,
        category: 'other',
        description: '',
        amount: 0,
        interest_rate: null,
      });
      setLiabilities((prev) => {
        const next = [...prev, row];
        liabilitiesRef.current = next;
        return next;
      });
      markSavedBriefly();
    } catch (err) {
      addToast({ type: 'error', title: 'Could not add liability', message: err?.message });
    } finally {
      setAdding(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      if (deleteTarget.kind === 'asset') {
        await api.financialAnalysis.assets.delete(deleteTarget.row.id);
        setAssets((prev) => {
          const next = prev.filter((r) => r.id !== deleteTarget.row.id);
          assetsRef.current = next;
          return next;
        });
      } else {
        await api.financialAnalysis.liabilities.delete(deleteTarget.row.id);
        setLiabilities((prev) => {
          const next = prev.filter((r) => r.id !== deleteTarget.row.id);
          liabilitiesRef.current = next;
          return next;
        });
      }
      setSaveStatus('saved');
      markSavedBriefly();
      setDeleteTarget(null);
    } catch (err) {
      addToast({ type: 'error', title: 'Could not delete row', message: err?.message });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">Assets</h2>
          <Button size="sm" variant="outline" onClick={addAsset} disabled={adding === 'asset'} isLoading={adding === 'asset'}>
            <SafeIcon icon={FiPlus} className="w-4 h-4 mr-1" />
            Add Asset
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="px-4 py-3 font-medium">Category</th>
                <th className="px-4 py-3 font-medium">Description</th>
                <th className="px-4 py-3 font-medium w-40">Amount</th>
                <th className="px-4 py-3 font-medium w-12" />
              </tr>
            </thead>
            <tbody>
              {(assets || []).length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-gray-400 text-sm">
                    No assets yet. Add one to get started.
                  </td>
                </tr>
              ) : (
                assets.map((row) => (
                  <tr key={row.id} className="border-b border-gray-50">
                    <td className="px-4 py-2">
                      <select
                        className={cellInput}
                        value={row.category || 'other'}
                        onChange={(e) => onAssetChange(row.id, 'category', e.target.value)}
                      >
                        {ASSET_CATEGORIES.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-2">
                      <input
                        className={cellInput}
                        value={row.description || ''}
                        placeholder="Description"
                        onChange={(e) => onAssetChange(row.id, 'description', e.target.value)}
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className={`${cellInput} tabular-nums`}
                        value={row.amount ?? 0}
                        onChange={(e) => onAssetChange(row.id, 'amount', e.target.value === '' ? 0 : Number(e.target.value))}
                      />
                    </td>
                    <td className="px-4 py-2">
                      <button
                        type="button"
                        className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"
                        onClick={() => setDeleteTarget({ kind: 'asset', row })}
                        aria-label="Delete asset"
                      >
                        <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">Liabilities</h2>
          <Button size="sm" variant="outline" onClick={addLiability} disabled={adding === 'liability'} isLoading={adding === 'liability'}>
            <SafeIcon icon={FiPlus} className="w-4 h-4 mr-1" />
            Add Liability
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="px-4 py-3 font-medium">Category</th>
                <th className="px-4 py-3 font-medium">Description</th>
                <th className="px-4 py-3 font-medium w-40">Amount</th>
                <th className="px-4 py-3 font-medium w-28">Interest %</th>
                <th className="px-4 py-3 font-medium w-12" />
              </tr>
            </thead>
            <tbody>
              {(liabilities || []).length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-400 text-sm">
                    No liabilities yet.
                  </td>
                </tr>
              ) : (
                liabilities.map((row) => (
                  <tr key={row.id} className="border-b border-gray-50">
                    <td className="px-4 py-2">
                      <select
                        className={cellInput}
                        value={row.category || 'other'}
                        onChange={(e) => onLiabilityChange(row.id, 'category', e.target.value)}
                      >
                        {LIABILITY_CATEGORIES.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-2">
                      <input
                        className={cellInput}
                        value={row.description || ''}
                        placeholder="Description"
                        onChange={(e) => onLiabilityChange(row.id, 'description', e.target.value)}
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className={`${cellInput} tabular-nums`}
                        value={row.amount ?? 0}
                        onChange={(e) => onLiabilityChange(row.id, 'amount', e.target.value === '' ? 0 : Number(e.target.value))}
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className={`${cellInput} tabular-nums`}
                        value={row.interest_rate ?? ''}
                        placeholder="—"
                        onChange={(e) =>
                          onLiabilityChange(
                            row.id,
                            'interest_rate',
                            e.target.value === '' ? null : Number(e.target.value),
                          )
                        }
                      />
                    </td>
                    <td className="px-4 py-2">
                      <button
                        type="button"
                        className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"
                        onClick={() => setDeleteTarget({ kind: 'liability', row })}
                        aria-label="Delete liability"
                      >
                        <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Balance Sheet Summary</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-400 font-medium">Total Assets</p>
            <p className="text-lg font-semibold text-gray-900 tabular-nums mt-1">{formatCurrency(totalAssets)}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-400 font-medium">Total Liabilities</p>
            <p className="text-lg font-semibold text-gray-900 tabular-nums mt-1">{formatCurrency(totalLiabilities)}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-400 font-medium">Net Worth</p>
            <p className={`text-lg font-semibold tabular-nums mt-1 ${netWorth < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
              {formatCurrency(netWorth)}
            </p>
          </div>
        </div>
      </Card>

      <Modal
        isOpen={Boolean(deleteTarget)}
        onClose={() => !deleting && setDeleteTarget(null)}
        title="Delete row?"
      >
        <p className="text-sm text-gray-500 mb-5">
          Remove this {deleteTarget?.kind === 'asset' ? 'asset' : 'liability'}
          {deleteTarget?.row?.description ? (
            <>
              {' '}
              (<span className="font-medium text-gray-800">{deleteTarget.row.description}</span>)
            </>
          ) : null}
          ? This cannot be undone.
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="danger" size="sm" onClick={confirmDelete} isLoading={deleting}>
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  );
}
